import { Routes } from '@angular/router'

import { AuthGuard } from '../../auth/auth-guard.service'

import { ArchiveGateComponent } from './archive-gate/archive-gate.component'
import { ArchiveHuntTemplateGuard } from './archived-hunt-template.guard'
import { ArchiveHuntGuard } from './archived-hunt.guard'
import { EditReportConclusionComponent } from './hunt-edit-report/edit-report-conclusion/edit-report-conclusion.component'
import { EditReportHuntPackageComponent } from './hunt-edit-report/edit-report-hunt-package/edit-report-hunt-package.component'
import { EditReportOverviewComponent } from './hunt-edit-report/edit-report-overview/edit-report-overview.component'
import { HuntEditReportComponent } from './hunt-edit-report/hunt-edit-report.component'
import { FinalReportConclusionComponent } from './hunt-final-report/final-report-conclusion/final-report-conclusion.component'
import { FinalReportCoverageComponent } from './hunt-final-report/final-report-coverage/final-report-coverage.component'
import { FinalReportHuntPackageComponent } from './hunt-final-report/final-report-hunt-package/final-report-hunt-package.component'
import { FinalReportOverviewComponent } from './hunt-final-report/final-report-overview/final-report-overview.component'
import { HuntFinalReportComponent } from './hunt-final-report/hunt-final-report.component'
import { HuntPackageDetailsComponent } from './hunt-package-details/hunt-package-details.component'
import { HuntRedirectComponent } from './hunt-redirect/hunt-redirect.component'
import { CustomHuntPackageComponent } from './hunt-template/custom-hunt-package/custom-hunt-package.component'
import { HuntTemplateComponent } from './hunt-template/hunt-template.component'
import { HuntWorkbenchComponent } from './hunt-workbench/hunt-workbench.component'
import { HuntManagementComponent } from './hunt.component'
import { DashboardComponent } from './overview/dashboard/dashboard.component'
import { HuntListComponent } from './overview/hunt-list/hunt-list.component'
import { HuntTemplateListComponent } from './overview/hunt-template-list/hunt-template-list.component'
import { OverviewComponent } from './overview/overview.component'
import { HuntResolver } from './resolvers/hunt.resolver'

export const HuntRoutes: Routes = [
  {
    path: '',
    component: HuntManagementComponent,
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',

    children: [
      // ───────────────────────────────────────────────────────────────────────────────
      // 1) Main “dashboard/hunts/hunt-templates” overview
      // ───────────────────────────────────────────────────────────────────────────────
      {
        path: '',
        component: OverviewComponent,
        runGuardsAndResolvers: 'always',
        children: [
          {
            path: 'dashboard',
            component: DashboardComponent,
            runGuardsAndResolvers: 'always',
          },
          {
            path: 'hunts',
            component: HuntListComponent,
            runGuardsAndResolvers: 'always',
          },
          {
            path: 'hunt-templates',
            component: HuntTemplateListComponent,
            runGuardsAndResolvers: 'always',
          },
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        ],
      },

      // ───────────────────────────────────────────────────────────────────────────────
      // 2) Hunt-template editor (no HuntResolver; uses its own guard)
      // ───────────────────────────────────────────────────────────────────────────────
      {
        path: 'hunt-template/:huntTemplateId',
        component: HuntTemplateComponent,
        canActivate: [ArchiveHuntTemplateGuard],
        runGuardsAndResolvers: 'always',
        children: [
          {
            path: 'custom-hunt-package',
            outlet: 'drawer',
            children: [
              { path: 'new', component: CustomHuntPackageComponent },
              { path: ':contentId', component: CustomHuntPackageComponent },
            ],
          },
        ],
      },
      {
        path: 'hunt-template/:huntTemplateId/custom-hunt-package',
        redirectTo: 'hunt-template/:huntTemplateId/custom-hunt-package/new',
        pathMatch: 'full',
      },

      // ───────────────────────────────────────────────────────────────────────────────
      // 3) Archive-gate (for any archived hunts or templates)
      // ───────────────────────────────────────────────────────────────────────────────
      {
        path: 'archive-gate',
        component: ArchiveGateComponent,
        runGuardsAndResolvers: 'always',
      },

      // ───────────────────────────────────────────────────────────────────────────────
      // 4) _Then_ catch-all “:id” for real hunts, with ArchiveGuard + Resolver
      //    – re-run on both param _and_ queryParam changes
      // ───────────────────────────────────────────────────────────────────────────────
      {
        path: ':id',
        canActivate: [ArchiveHuntGuard],
        resolve: { hunt: HuntResolver },
        runGuardsAndResolvers: 'always',
        children: [
          // – default “go to workbench” –
          {
            path: '',
            component: HuntRedirectComponent,
            runGuardsAndResolvers: 'always',
          },

          // – workbench –
          {
            path: 'workbench',
            runGuardsAndResolvers: 'always',
            children: [
              { path: '', component: HuntWorkbenchComponent },
              {
                path: 'package/:packageId',
                component: HuntPackageDetailsComponent,
                runGuardsAndResolvers: 'always',
              },
            ],
          },

          // – edit-report –
          {
            path: 'edit-report',
            component: HuntEditReportComponent,
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: 'overview',
                component: EditReportOverviewComponent,
                runGuardsAndResolvers: 'always',
              },
              {
                path: 'conclusion',
                component: EditReportConclusionComponent,
                runGuardsAndResolvers: 'always',
              },
              {
                path: 'technical-summary/:packageId',
                component: EditReportHuntPackageComponent,
                runGuardsAndResolvers: 'always',
              },
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
            ],
          },

          // – final-report –
          {
            path: 'final-report',
            component: HuntFinalReportComponent,
            runGuardsAndResolvers: 'always',
            children: [
              {
                path: 'overview',
                component: FinalReportOverviewComponent,
                runGuardsAndResolvers: 'always',
              },
              {
                path: 'conclusion',
                component: FinalReportConclusionComponent,
                runGuardsAndResolvers: 'always',
              },
              {
                path: 'technical-summary/:packageId',
                component: FinalReportHuntPackageComponent,
                runGuardsAndResolvers: 'always',
              },
              {
                path: 'coverage',
                component: FinalReportCoverageComponent,
                runGuardsAndResolvers: 'always',
              },
              { path: '', redirectTo: 'overview', pathMatch: 'full' },
            ],
          },
        ],
      },
    ],
  },
]
